import { FC } from 'react';
import { RotateCcw, RotateCw } from 'react-feather';
import { useEditorData } from '../../lib/FabricJs';
import { IFRAME_WIDGETS } from '../../config';
import { rotateInPlace } from '../../utils/objectGeometry';
import Section, { ButtonGrid, PanelButton } from './Section';
import { useSelectionEdit } from './useSelectionEdit';

const iconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const FlipRotate: FC = () => {
  const { canvas } = useEditorData();
  const editSelection = useSelectionEdit();

  const objects = canvas?.getActiveObjects() || [];
  // Widgets are HTML laid over the canvas and cannot be shown flipped or
  // rotated; locked objects keep their orientation.
  const editable = objects.filter(
    (object) =>
      !Object.values(IFRAME_WIDGETS).includes(object.data?.type) &&
      !object.lockRotation,
  );
  if (!editable.length) return null;

  const flip = (property: 'flipX' | 'flipY') =>
    editSelection(() =>
      editable.forEach((object) => object.set(property, !object[property])),
    );
  const rotateBy = (degrees: number) =>
    editSelection(() =>
      editable.forEach((object) =>
        rotateInPlace(object, (object.angle ?? 0) + degrees),
      ),
    );

  return (
    <Section title="Flip & rotate">
      <ButtonGrid columns={4}>
        <PanelButton
          aria-label="Flip horizontally"
          title="Flip horizontally"
          onClick={() => flip('flipX')}
        >
          <svg {...iconProps}>
            <path d="M12 3v18M8 7l-5 5 5 5V7zM16 7l5 5-5 5V7z" />
          </svg>
        </PanelButton>
        <PanelButton
          aria-label="Flip vertically"
          title="Flip vertically"
          onClick={() => flip('flipY')}
        >
          <svg {...iconProps}>
            <path d="M3 12h18M7 8l5-5 5 5H7zM7 16l5 5 5-5H7z" />
          </svg>
        </PanelButton>
        <PanelButton
          aria-label="Rotate 90° counter-clockwise"
          title="Rotate 90° counter-clockwise"
          onClick={() => rotateBy(-90)}
        >
          <RotateCcw size={16} />
        </PanelButton>
        <PanelButton
          aria-label="Rotate 90° clockwise"
          title="Rotate 90° clockwise"
          onClick={() => rotateBy(90)}
        >
          <RotateCw size={16} />
        </PanelButton>
      </ButtonGrid>
    </Section>
  );
};

export default FlipRotate;
